/* =====================================================================
   World drivers — the bridge from World3 macro trajectories to the
   entity micro-simulation. For each scenario we derive five entity-
   facing series over the simulation window (2026..2076), normalized to
   their 2026 value:

     demand      sector-weighted world demand (industry/services/food/pop)
     scarcity    input-cost index (fraction of capital diverted to
                 resource extraction, fcaor, relative to 2026)
     pollution   persistent-pollution burden relative to 2026
     hazard      turbulence: contraction rate of the world economy plus
                 food-system stress (expected-shock intensity, >= 0)
     transition  pressure/opportunity to shift toward efficient,
                 diversified, regenerative value creation (0..1)

   Deterministic, cached per scenario. The trace decomposition (§6.4)
   freezes these one at a time to attribute score changes to drivers.
   ===================================================================== */
import { sectorWeights, type SectorWeights } from '@flourish/schema';
import { series, W3_PARAMS, type ScenarioId } from './scenarios.js';

export const SIM_START = 2026;
export const SIM_YEARS = 50;                       // 2026..2076 inclusive -> 51 points
export const HORIZONS = [5, 15, 30, 50] as const;

export interface WorldDrivers {
  scenarioId: ScenarioId;
  years: number[];                                  // 2026..2076
  /** demand components, each normalized to 2026 = 1 */
  dIndustry: number[];
  dServices: number[];
  dFood: number[];
  dPopulation: number[];
  /** equal-weight blend of the four components (diversified exposure) */
  demandBroad: number[];
  scarcity: number[];
  pollution: number[];
  hazard: number[];
  transition: number[];
}

const _cache = new Map<ScenarioId, WorldDrivers>();

export function worldDrivers(scenarioId: ScenarioId): WorldDrivers {
  const hit = _cache.get(scenarioId);
  if (hit) return hit;

  const w = series(scenarioId);
  const i0 = w.years.indexOf(SIM_START);
  const n = SIM_YEARS + 1;

  const norm = (arr: number[], i: number) => arr[i0 + i] / arr[i0];

  const years: number[] = [];
  const dIndustry: number[] = [], dServices: number[] = [], dFood: number[] = [], dPopulation: number[] = [];
  const demandBroad: number[] = [], scarcity: number[] = [], pollution: number[] = [];
  const hazard: number[] = [], transition: number[] = [];

  // service output total = sopc * pop
  const so0 = w.sopc[i0] * w.pop[i0];

  const params = W3_PARAMS[scenarioId];
  const policyYear = params.policyYear ?? 4000;

  for (let t = 0; t < n; t++) {
    years.push(SIM_START + t);
    const ind = norm(w.io, t);
    const serv = (w.sopc[i0 + t] * w.pop[i0 + t]) / so0;
    const food = norm(w.food, t);
    const pop = norm(w.pop, t);
    dIndustry.push(ind); dServices.push(serv); dFood.push(food); dPopulation.push(pop);
    demandBroad.push((ind + serv + food + pop) / 4);

    scarcity.push(w.fcaor[i0 + t] / w.fcaor[i0]);
    pollution.push(w.ppolx[i0 + t] / Math.max(w.ppolx[i0], 1e-9));
  }

  for (let t = 0; t < n; t++) {
    // turbulence: rate of world contraction (log-derivative of broad demand)
    // plus food-system stress vs the 2026 baseline
    const dln = t === 0 ? 0 : Math.log(demandBroad[t]) - Math.log(demandBroad[t - 1]);
    const foodStress = Math.max(0, 1 - (w.fpc[i0 + t] / w.fpc[i0]));
    hazard.push(3.0 * Math.max(0, -dln) + 0.5 * foodStress);

    // transition pressure/opportunity: binding limits (costly inputs,
    // pollution load) + explicit societal stabilization policies
    const pressure = 0.5 * Math.max(0, scarcity[t] - 1) + 0.35 * Math.max(0, (pollution[t] - 1) / 3);
    const policy = policyYear >= 4000 ? 0
      : Math.max(0, Math.min(1, (SIM_START + t - Math.max(SIM_START, policyYear)) / 10 +
          (policyYear <= SIM_START ? 1 : 0)));
    transition.push(Math.max(0, Math.min(1, pressure + 0.6 * policy)));
  }

  const out: WorldDrivers = {
    scenarioId, years, dIndustry, dServices, dFood, dPopulation,
    demandBroad, scarcity, pollution, hazard, transition,
  };
  _cache.set(scenarioId, out);
  return out;
}

/** Sector-weighted core demand at step t for given weights. */
export function demandCore(d: WorldDrivers, wts: SectorWeights, t: number): number {
  return wts.ind * d.dIndustry[t] + wts.serv * d.dServices[t] +
         wts.food * d.dFood[t] + wts.pop * d.dPopulation[t];
}

/** Year (calendar) at which core demand peaks for a sector mix. */
export function demandPeakYear(d: WorldDrivers, wts: SectorWeights): number {
  let best = -Infinity, y = d.years[0];
  for (let t = 0; t < d.years.length; t++) {
    const v = demandCore(d, wts, t);
    if (v > best) { best = v; y = d.years[t]; }
  }
  return y;
}

export { sectorWeights };
